// @ts-check
// Partes de um número de relatório (contrato 07, seção 7.1, grupo de números v2): valor (moeda compacta a partir de
// R$ 10 mil, com o valor completo na dica), legenda com "agora" para a carteira de agora, seta contra o mesmo
// intervalo do ano anterior (melhor ou pior conforme `lowerIsBetter`), barra da meta e legenda secundária. Puro, sem
// Preact, para os testes do cliente e para o CSV.
import { hasTranslation, t } from '../../i18n/index.js';
import { formatNumber } from '../../lib/format.js';
import { computeVariation, formatMetricValue } from './widget-format.js';

/**
 * @typedef {{ key: string, value: number | null, format?: string, tone?: string, previous?: number | null, lowerIsBetter?: boolean,
 *   goal?: { value: number, progress: number, since: string | null }, secondary?: { key: string, value: number | string | null, count?: number }, scope?: 'NOW' }} ReportStat
 */

/** A partir deste valor a moeda sai compacta ("R$ 229,1 mil"). */
export const COMPACT_CURRENCY_FROM = 10_000;

/**
 * Texto do valor e a dica com o valor completo quando ele foi encurtado.
 * @param {ReportStat} stat
 */
export function formatStatValue(stat) {
  const full = formatMetricValue(stat.value, stat.format);
  const isCompact = stat.format === 'CURRENCY' && typeof stat.value === 'number' && Math.abs(stat.value) >= COMPACT_CURRENCY_FROM;
  return { text: isCompact ? formatMetricValue(stat.value, 'CURRENCY', { compact: true }) : full, title: full };
}

/**
 * Legenda do número, com " · agora" nos números que não seguem o período.
 * @param {ReportStat} stat
 */
export function statLabelText(stat) {
  const label = t(`reports.stats.${stat.key}`);
  return stat.scope === 'NOW' ? `${label} · ${t('reports.statScope.NOW')}` : label;
}

/**
 * Seta contra o mesmo intervalo do ano anterior; nula sem `previous`. `quality` diz a cor: melhor, pior ou igual.
 * `shortText` é o que cabe num cartão estreito ("+22%"): o subtítulo do cartão já diz contra o que compara.
 * @param {ReportStat} stat
 * @returns {{ trend: 'up' | 'down' | 'same', quality: 'better' | 'worse' | 'same', text: string, shortText: string, ariaLabel: string, title: string } | null}
 */
export function describeStatDelta(stat) {
  if (stat.previous === null || stat.previous === undefined || stat.value === null || stat.value === undefined) return null;
  const title = t('dashboards.number.previousTooltip', { value: formatMetricValue(stat.previous, stat.format) });
  const qualityOf = (/** @type {'up' | 'down' | 'same'} */ trend) => (trend === 'same' ? 'same' : (trend === 'up') !== Boolean(stat.lowerIsBetter) ? 'better' : 'worse');
  if (Number(stat.previous) === 0 && Number(stat.value) !== 0) {
    const trend = Number(stat.value) > 0 ? 'up' : 'down';
    return { trend, quality: qualityOf(trend), text: t('reports.variation.fromZero'), shortText: t('reports.variation.fromZero'), ariaLabel: title, title };
  }
  const variation = computeVariation(stat.value, stat.previous);
  if (variation === null) return null;
  if (variation === 0) return { trend: 'same', quality: 'same', text: t('reports.variation.same'), shortText: t('reports.variation.sameShort'), ariaLabel: t('reports.variation.same'), title };
  const percent = formatNumber(Math.abs(variation), { percentage: true, decimals: Math.abs(variation) < 10 ? 1 : 0 });
  const trend = variation > 0 ? 'up' : 'down';
  const signed = `${variation > 0 ? '+' : '-'}${percent}`;
  return {
    trend,
    quality: qualityOf(trend),
    text: t('reports.variation.text', { value: signed }),
    shortText: signed,
    ariaLabel: t(trend === 'up' ? 'reports.variation.ariaUp' : 'reports.variation.ariaDown', { value: percent }),
    title,
  };
}

/**
 * Barra da meta da campanha: parte preenchida, se já bateu e o texto "64 de 980 desde 01/08/2026" ("64 de 980" no
 * cartão estreito).
 * @param {ReportStat} stat
 * @returns {{ share: number, isComplete: boolean, text: string, shortText: string, title: string, minimum: number, maximum: number, now: number } | null}
 */
export function describeStatGoal(stat) {
  const goal = stat.goal;
  if (!goal || !(Number(goal.value) > 0)) return null;
  const progress = Number(goal.progress ?? 0);
  const share = (progress / Number(goal.value)) * 100;
  const since = goal.since ? `${goal.since.slice(8, 10)}/${goal.since.slice(5, 7)}/${goal.since.slice(0, 4)}` : '';
  return {
    share: Math.min(100, Math.max(0, share)),
    isComplete: share >= 100,
    text: t('dashboards.number.goalSince', { progress: formatMetricValue(progress, 'NUMBER'), goal: formatMetricValue(goal.value, 'NUMBER'), since }),
    // cartão estreito: sem a data de início (que fica na dica)
    shortText: t('dashboards.number.goalShort', { progress: formatMetricValue(progress, 'NUMBER'), goal: formatMetricValue(goal.value, 'NUMBER') }),
    title: t('dashboards.number.goalTooltip', { share: formatNumber(share, { percentage: true, decimals: 0 }) }),
    minimum: 0,
    maximum: Number(goal.value),
    now: progress,
  };
}

/**
 * Legenda secundária ("30 famílias", "6 agendadas", "mais comum: Mensalidade"); nula sem valor.
 * @param {ReportStat} stat
 */
export function describeStatSecondary(stat) {
  const secondary = stat.secondary;
  if (!secondary || secondary.value === null || secondary.value === undefined || secondary.value === '') return null;
  if (secondary.key === 'topReason') return t('reports.statSecondary.topReason', { value: secondary.value });
  return t(`reports.statSecondary.${secondary.key}`, { count: Number(secondary.value) });
}

/**
 * Linha explicativa abaixo do título, com os números do resumo. O número que leva substantivo vem de
 * `reports.subtitleParts.<relatório>.<campo>`, com plural ("1 aluno perdido", "3 alunos perdidos").
 * @param {string | undefined} report @param {any} data
 */
export function getReportSubtitle(report, data) {
  if (!report || !hasTranslation(`reports.subtitles.${report}`)) return null;
  const summary = data?.summary ?? {};
  /** @type {Record<string, unknown>} */
  const values = {};
  for (const [key, value] of Object.entries(summary)) {
    const partKey = `reports.subtitleParts.${report}.${key}`;
    if (typeof value === 'number' && hasTranslation(partKey)) values[key] = t(partKey, { count: value });
    else values[key] = typeof value === 'number' ? formatMetricValue(value, key.endsWith('Rate') ? 'PERCENT' : 'NUMBER') : value;
  }
  return t(`reports.subtitles.${report}`, values);
}
